import { Injectable } from '@angular/core';
import { ListDetailManagementService } from '@app/lists/shared/list-detail-management/services/list-detail-management.service';
import {
  PopoverController,
  ActionSheetController,
  ModalController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState } from '@panamax/app-state';
import { ListsOverlayService } from '../../../lists/services/lists-overlay.service';
import { ProductAlternativeSearchService } from '../../../product-detail/services/product-alternative-search.service';
import { ProductDetailTagsService } from '../../../product-detail/services/product-detail-tags.service';
import { PdpLnkSrc } from '../../constants/pdp-lnksrc.enum';
import { NavigationHelperService } from '../../helpers/navigation.helpers.service';
import { EllipsisClickData } from '../../models/usf-product-card.model';
import { ModalService } from '../modal/modal.service';
import { ProductCardService } from './product-card.service';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { DidYouForgetCategory } from '@app/did-you-forget/constants/did-you-forget-category.enum';

@Injectable({
  providedIn: 'root',
})
export class DyfProductCardService extends ProductCardService {
  constructor(
    actionSheetController: ActionSheetController,
    modalController: ModalController,
    popoverController: PopoverController,
    panAppState: PanAppState,
    translateService: TranslateService,
    listsOverlayService: ListsOverlayService,
    modalService: ModalService,
    navigationHelperService: NavigationHelperService,
    productAlternativeSearchService: ProductAlternativeSearchService,
    productDetailTagsService: ProductDetailTagsService,
    listDetailManagementService: ListDetailManagementService,
    analyticsService: ProductAnalyticsService,
    toggleNewSearchService: ToggleNewSearchService,
  ) {
    super(
      actionSheetController,
      modalController,
      popoverController,
      panAppState,
      translateService,
      listsOverlayService,
      modalService,
      navigationHelperService,
      productAlternativeSearchService,
      productDetailTagsService,
      listDetailManagementService,
      analyticsService,
      toggleNewSearchService,
    );
  }

  routeToPDP(
    productNumber: number,
    isQuickView: boolean,
    rank?: number,
    dyfCategory?: DidYouForgetCategory,
  ) {
    let lnksrc;
    switch (dyfCategory) {
      case DidYouForgetCategory.InspiredPick:
        lnksrc = isQuickView
          ? PdpLnkSrc.inspiredPickQuickView.valueOf()
          : PdpLnkSrc.inspiredPicks.valueOf();
        break;
      case DidYouForgetCategory.MerchZone:
        lnksrc = isQuickView
          ? PdpLnkSrc.topSellersReviewOrderQuickView.valueOf()
          : PdpLnkSrc.topSellersReviewOrder.valueOf();
        break;
      default:
        lnksrc = isQuickView
          ? PdpLnkSrc.dyfQuickView.valueOf()
          : PdpLnkSrc.dyf.valueOf();
        break;
    }
    this.navigationHelperService.routeToProductDetailsQueryParams(
      productNumber,
      { lnksrc, merchRank: rank },
    );
  }

  constructEllipsisButtons(
    ellipsisClickData: EllipsisClickData,
    rank?: number,
    dyfCategory?: DidYouForgetCategory,
    originSearchPage?: string,
  ) {
    let buttons = [];
    if (this.shouldAddSearchForSubs(ellipsisClickData.product)) {
      buttons.push(
        this.getSearchForSubsBtn(ellipsisClickData, originSearchPage),
      );
    }
    buttons.push(
      this.getQuickViewBtn(
        ellipsisClickData,
        originSearchPage,
        rank,
        dyfCategory,
      ),
    );
    if (this.shouldAddProductStatus(ellipsisClickData.product)) {
      buttons.push(this.getProductStatusBtn(ellipsisClickData));
    }
    this.getSellerQuickViewBtn(buttons, ellipsisClickData);
    this.getCompareBtn(buttons, ellipsisClickData);
    return buttons;
  }

  async openQuickViewModal(
    ellipsisClickData: EllipsisClickData,
    selectedSegment: string,
    originSearchPage?: string,
    rank?: number,
    dyfCategory?: DidYouForgetCategory,
  ) {
    const modal = await this.getQuickViewModal(
      ellipsisClickData,
      selectedSegment,
      originSearchPage,
    );

    const quickViewModal = await this.modalController.create(modal);

    quickViewModal.onDidDismiss().then((resp: any) => {
      if (!!resp?.data) {
        this.routeToPDP(resp.data, true, rank, dyfCategory);
      }
    });
    return await quickViewModal.present();
  }

  getQuickViewBtn(
    ellipsisClickData: EllipsisClickData,
    originSearchPage?: string,
    rank?: number,
    dyfCategory?: DidYouForgetCategory,
  ) {
    return {
      text: this.translateService.instant(
        'i18n.productDetail.productQuickView',
      ),
      handler: () => {
        this.openQuickViewModal(
          ellipsisClickData,
          'quickView',
          originSearchPage,
          rank,
          dyfCategory,
        );
        this.dismissProductCardOptions(ellipsisClickData.isTouch);
      },
    };
  }
}
